import { useCallback, useMemo, useState } from 'react';
import { FastField, Form, Formik, FormikHelpers } from 'formik';
import useSWR, { KeyedMutator } from 'swr';
import { Box, Button, IconButton, Stack, useTheme } from '@mui/material';
import { DuplicateIcon, PhoneIcon } from '@heroicons/react/outline';
import { CreditScore } from '@modules/ContactManager/components/Header/components/CreditScore';
import { ContactForm } from '@modules/ContactManager/components/Tabs/components/Contact/ContactForm';
import { NContact, coApplicantValidationSchema } from '@modules/ContactManager/components/Tabs/components/Contact/validation';
import {
  requestCreateCoApplicant,
  requestUpdateCoApplicant,
} from '@modules/ContactManager/components/Tabs/components/Contact/queries';
import { CreditBureauScoreType, EBureauType, IContactHeaderData } from '@modules/ContactManager/components/Header/interfaces';
import { DetailedCreditReport } from '@modules/ContactManager/components/Header/components/DetailedCreditReport';
import { CoApplicantErrorMessage } from '@modules/ContactManager/components/constants';

import { Loader } from '@shared/enhancers/withLoader';
import { IRichModalProps } from '@shared/components/modals/RichModal';
import { SERVICES } from '@shared/constants/apiConfig';
import { CheckboxField } from '@shared/fields/CheckboxField';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { CoApplicantModalHeader, CreditScoreSection, ModalHeaderInputs } from '@styles/ContactManager/ContactStyles';

import { Modal } from '@shared/components/modals/Modal';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { colorTheme } from '@styles/muiTheme';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { Roles } from '@shared/constants/constants';
import { useConfirmationModal } from '@shared/hooks/useConfirmationModal';
import { ContactTabHeader } from '@styles/ContactManager/TabsStyles';
import { copyToClipboard, filterEmptyStrings, formatPhoneNumber } from '@shared/commonFunctions';
import { Contrast } from '@styles/ContactManager/HeaderStyles';
import { ContactChatModal } from '@modules/ContactManager/components/Header/components/ContactChat/ContactChatModal';
import { incomeTypes } from '@shared/constants/lists';
import { SaveButton } from '@shared/inputs/Buttons';
import { IMap } from '@shared/interfaces';
import { UnableToSaveModal } from '@modules/ContactManager/components/Tabs/shared/UnableToSaveModal';
import { relationshipTypes } from '../../interfaces';
import { OptOutModal } from '../OptOutModal/OptOutModal';
import { IContactDetails } from '../OptOutModal/interfaces';
import { IContact } from '../../../Deals/interfaces';
import { RelationshipEnum } from '../../../Deals/components/CoBorrower/types';
import { ICoApplicant } from './validation';


interface ICoApplicantModalProps extends IRichModalProps {
  contactId: string;
  contactInfo?: NContact.Entity;
  coApplicantIdToEdit: string;
  reloadContact?: KeyedMutator<NContact.Entity>;
  onClose: () => void;
  addresses?: NContact.Address[];
  coApplicantModalPosition: number;
}

export const getRelationship = (data?: ICoApplicant | IContact) => {
  const rel =
    data && 'relationshipType' in data
      ? data.relationshipType
      : data && 'relationShip' in data
      ? data.relationShip
      : RelationshipEnum.None;

  return rel === '' || rel == null 
    ? RelationshipEnum.None
    : rel;
};

export const CoApplicantModal = ({
  addresses = [],
  coApplicantIdToEdit,
  contactId,
  contactInfo,
  onClose,
  reloadContact,
  coApplicantModalPosition,
  ...otherProps
}: ICoApplicantModalProps) => {
  const theme = useTheme();
  const { isAdmin, userHasRoles } = useUserAccount();
  const { isOpen, onCancel, onConfirm, openDialog } = useConfirmationModal();
  const [resetPosition, setResetPositon] = useState(0);
  const [coApplicantId, setCoApplicantId] = useState(coApplicantIdToEdit);
  const [creditReports, setCreditReports] = useState<IMap<number>>({});
  const [pulledFicoTypes, setPulledFicoTypes] = useState<CreditBureauScoreType[]>([]);
  const [optOutModalIsOpen, setOptOutModalIsOpen] = useState(0);
  const canPullAll = isAdmin || userHasRoles([Roles.CREDIT_REPORTS_PULL_ALL]);
  const canPullExperian = userHasRoles([Roles.CREDIT_REPORTS_PULL_EXPERIAN]);
  const canPullTransunion = userHasRoles([Roles.CREDIT_REPORTS_PULL_TRANSUNION]);
  const canPullEquifax = userHasRoles([Roles.CREDIT_REPORTS_PULL_EQUIFAX]);
  const canViewAll = userHasRoles([Roles.CREDIT_REPORTS_VIEW_ALL]);

  const isNewCoApplicant = coApplicantId === 'new';

  const {
    data: coApplicant,
    isLoading: isCoApplicantLoading,
    mutate: reloadCoApplicant,
  } = useSWR<ICoApplicant>(
    coApplicantId !== 'new' && `/${SERVICES.CONTACT}/contacts/${contactId}/co-applicants/${coApplicantId}`
  );

  const { data: contactDetailsData } = useSWR<IContactHeaderData>(
    !isNewCoApplicant && `/${SERVICES.CONTACT}/contacts/${coApplicantId}/header`
  );

  const initialValues = useMemo(() => {
    const firstName = coApplicant?.firstName ?? '';
    const lastName = coApplicant?.lastName ?? '';
    const otherIncomes = (coApplicant?.otherIncomes || []).map((income) => ({
      ...income,
      incomeType: incomeTypes.find((type) => type.label.replace(/\s+/g, '') === income.incomeType)?.value || incomeTypes[0]?.value,
    }));

    return {
      ...coApplicant,
      firstName,
      lastName,
      otherIncomes,
      id: coApplicant?.id,
      relationshipType: getRelationship(coApplicant)
    };
  }, [coApplicant, incomeTypes, relationshipTypes, coApplicant]);

  const contactDetails :IContactDetails ={
    firstName:coApplicant?.firstName??'',
    lastName:coApplicant?.lastName??'',
    email:coApplicant?.email??'',
    cellPhone:coApplicant?.cellPhone??''
  };

  const handleSubmit = useCallback(
    async (data: ICoApplicant, formikHelpers: FormikHelpers<ICoApplicant>) => {
      if (data.id) {
        const previousCoApplicantData = contactInfo?.coApplicants.find(co => co.id === data.id);
        if (previousCoApplicantData?.email != data.email && previousCoApplicantData?.cellPhone != data.cellPhone && data?.email == contactInfo?.email && data?.cellPhone == contactInfo?.cellPhone) {
          formikHelpers.setErrors({
            email: CoApplicantErrorMessage.EMAIL_MATCHING_ERROR_MESSAGE,
            cellPhone: CoApplicantErrorMessage.CELL_PHONE_MATCHING_ERROR_MESSAGE
          });
        }
        else {
          if (previousCoApplicantData?.email != data.email && data?.email == contactInfo?.email) {
            formikHelpers.setErrors({ email: CoApplicantErrorMessage.EMAIL_MATCHING_ERROR_MESSAGE });
          }
          else if (previousCoApplicantData?.cellPhone != data.cellPhone && data?.cellPhone == contactInfo?.cellPhone) {
            formikHelpers.setErrors({ cellPhone: CoApplicantErrorMessage.CELL_PHONE_MATCHING_ERROR_MESSAGE });
          }
          else {
            const requestModel = filterEmptyStrings({
                  ...data,
                  firstName: data.firstName,
                  middleInitial: data.middleInitial,
                  lastName: data.lastName,
                  suffix: data.suffix,
                  maidenName: data.maidenName,
                  nickName: data.nickName,
                  ssn: data.ssn,
                  dateOfBirth: data.dateOfBirth,
                  homePhone: data.homePhone,
                  workPhone: data.workPhone,
                  workPhoneExtension: data.workPhoneExtension ? '' + data.workPhoneExtension : null,
                  cellPhone: data.cellPhone,
                  email: data.email,
                  militaryStatus: data.militaryStatus,
                  maritalStatus: data.maritalStatus,
                  driverLicenseNumber: data.driverLicenseNumber,
                  driverLicenseExpireDate: data.driverLicenseExpireDate,
                  driverLicenseIssuesOnDate: data.driverLicenseIssuesOnDate,
                  driverLicenseState: data.driverLicenseState,    
            });
            await requestUpdateCoApplicant(contactId, {
              ...requestModel,
              relationshipType: getRelationship(data)
            });
            
            //Added 1 sec delay after updating email
            await new Promise((resolve) => setTimeout(resolve, 1000));
            reloadCoApplicant();
          }
        }
      } else {
        if (data?.email == contactInfo?.email && data?.cellPhone == contactInfo?.cellPhone) {
          formikHelpers.setErrors({
            email: CoApplicantErrorMessage.EMAIL_MATCHING_ERROR_MESSAGE,
            cellPhone: CoApplicantErrorMessage.CELL_PHONE_MATCHING_ERROR_MESSAGE
          });
        }
        else {
          if (data?.email == contactInfo?.email) {
            formikHelpers.setErrors({ email: CoApplicantErrorMessage.EMAIL_MATCHING_ERROR_MESSAGE });
          }
          else if (data?.cellPhone == contactInfo?.cellPhone) {
            formikHelpers.setErrors({ cellPhone: CoApplicantErrorMessage.CELL_PHONE_MATCHING_ERROR_MESSAGE });
          }
          else {
            const result = await openDialog();

            const response = await requestCreateCoApplicant(contactId, {
              ...data,
              addresses: result ? addresses : [],
            });

            setCoApplicantId(response.id);

            formikHelpers.resetForm({
              values: {
                ...response,
                ssn: data.ssn,
              },
            });
          }
        }
      }
      reloadContact?.();
    },
    [addresses, contactId, contactInfo]
  );

  const onOpenCreditReport = useCallback(
    async (bureauType: EBureauType, experianFicoTypeArray : CreditBureauScoreType[]) => {
      if (
        ((bureauType === EBureauType.Experian || bureauType === EBureauType.ExperianFico) && canPullExperian) ||
        (bureauType === EBureauType.Transunion && canPullTransunion) ||
        (bureauType === EBureauType.Equifax && canPullEquifax) ||
        canPullAll ||
        canViewAll
      ) {
        setCreditReports((prevState) => ({
          ...prevState,
          [bureauType]: (prevState[bureauType] || 0) + 1,
        }));
        setPulledFicoTypes(experianFicoTypeArray);
        return;
      }
      Notistack.generalError('You don\'t have permissions to view credit reports.');
    },
    [canPullExperian, canPullTransunion, canPullEquifax, canPullAll, canViewAll]
  );

  const onCloseCreditReport = useCallback((bureauType: string) => {
    setCreditReports((prevState) => {
      const newState = { ...prevState };
      delete newState[bureauType];
      return newState;
    });
  }, []);

  const phoneNumber = useMemo(
    () => coApplicant?.cellPhone || coApplicant?.homePhone || coApplicant?.workPhone,
    [coApplicant?.cellPhone, coApplicant?.homePhone, coApplicant?.workPhone]
  );

  return (
    <>
      {isOpen && (
        <AlertModal
          open
          title="Address Notification"
          subtitle="Does contact share same address as borrower?"
          buttonText={'Yes'}
          cancelButtonText={'No'}
          onConfirm={onConfirm}
          onClose={onCancel}
        />
      )}
      {creditReports[EBureauType.Experian] && (
        <Modal
          draggable
          hideBackdrop
          open
          title={EBureauType.Experian}
          dialogWidth="42%"
          dialogHeight="95%"
          resetPosition={creditReports[EBureauType.Experian]}
          onClose={() => onCloseCreditReport(EBureauType.Experian)}
        >
          <DetailedCreditReport
            bureauType={EBureauType.Experian}
            contactId={coApplicantId}
            pulledFicoTypes={pulledFicoTypes}
          />
        </Modal>
      )}

      {creditReports[EBureauType.Equifax] && (
        <Modal
          draggable
          hideBackdrop
          open
          title={EBureauType.Equifax}
          dialogWidth="42%"
          dialogHeight="95%"
          resetPosition={creditReports[EBureauType.Equifax]}
          onClose={() => onCloseCreditReport(EBureauType.Equifax)}
        >
          <DetailedCreditReport
            bureauType={EBureauType.Equifax}
            contactId={coApplicantId}
            pulledFicoTypes={pulledFicoTypes}
          />
        </Modal>
      )}

      {creditReports[EBureauType.Transunion] && (
        <Modal
          draggable
          hideBackdrop
          open
          title={EBureauType.Transunion}
          dialogWidth="42%"
          dialogHeight="95%"
          resetPosition={creditReports[EBureauType.Transunion]}
          onClose={() => onCloseCreditReport(EBureauType.Transunion)}
        >
          <DetailedCreditReport
            bureauType={EBureauType.Transunion}
            contactId={coApplicantId}
            reloadContact={reloadCoApplicant}
            pulledFicoTypes={pulledFicoTypes}
          />
        </Modal>
      )}
      
      <Modal
        {...otherProps}
        hideBackdrop
        dialogWidth="1333px"
        resetPosition={coApplicantModalPosition}
        onClose={onClose}
      >
        {isCoApplicantLoading ? (
          <Loader />
        ) : (
          <Formik
            enableReinitialize
            initialValues={initialValues as ICoApplicant}
            validationSchema={coApplicantValidationSchema}
            onSubmit={handleSubmit}
          >
            {(props) => (
              <Form style={{ position: 'initial' }}>
                <UnableToSaveModal />
                <CoApplicantModalHeader>
                  <ModalHeaderInputs>
                    <Box
                      bgcolor={colorTheme.palette.common.white}
                      height="42px"
                      borderRadius="4px"
                    >
                      <FastField
                        name="relationshipType"
                        label="Relationship Type"
                        options={relationshipTypes}
                        component={SelectAutocompleteField}
                      />
                    </Box>
                    <FastField
                      name="isDroppedContact"
                      label="Dropped Co-Borrower"
                      component={CheckboxField}
                    />

                    {!!phoneNumber && (
                      <Box
                        display="flex"
                        alignItems="center"
                      >
                        <Contrast
                          bold
                          onClick={() => setResetPositon((prevState) => prevState + 1)}
                        >
                          {formatPhoneNumber(phoneNumber)}
                        </Contrast>

                        <IconButton onClick={() => copyToClipboard(phoneNumber)}>
                          <DuplicateIcon
                            width={18}
                            color={theme.palette.info.light}
                          />
                        </IconButton>

                        <IconButton>
                          <PhoneIcon
                            width={18}
                            color={theme.palette.info.light}
                          />
                        </IconButton>
                      </Box>
                    )}
                  </ModalHeaderInputs>
                  <Modal
                    hideBackdrop
                    title="SMS History"
                    dialogHeight="800px"
                    dialogWidth="530px"
                    open={!!resetPosition}
                    resetPosition={resetPosition}
                    onClose={() => setResetPositon(0)}
                  >
                    {phoneNumber && (
                      <ContactChatModal
                        contactNumber={phoneNumber}
                        contactId={coApplicantId}
                        contactDetails={contactDetails}
                      />
                    )}
                  </Modal>

                  {props.values.id && (
                    <CreditScoreSection>
                      <CreditScore
                        data={contactDetailsData?.creditScore}
                        contactId={props.values.id}
                        onOpenCreditReport={onOpenCreditReport}
                      />
                    </CreditScoreSection>
                  )}
                </CoApplicantModalHeader>

                <ContactTabHeader>
                  <Stack
                    direction="row"
                    sx={{ alignItems: 'center', gap: 1 }}
                  >
                    <Button
                      disabled={!props.values.coApplicantId}
                      variant="contained"
                      size="small"
                      onClick={() => setOptOutModalIsOpen((prevState) => prevState + 1)}
                    >
                      Consent
                    </Button>
                  </Stack>

                  <Stack
                    direction="row"
                    sx={{ alignItems: 'center', gap: 1 }}
                  >
                    <SaveButton
                      size="small"
                      width="65px"
                      isLoading={props.isSubmitting}
                    />
                  </Stack>

                  {!!optOutModalIsOpen && (
                    <OptOutModal
                      optOutModalPosition={optOutModalIsOpen}
                      handleClose={() => setOptOutModalIsOpen(0)}
                      contactDetails={contactDetails}
                    />
                  )}
                </ContactTabHeader>

                <ContactForm
                  onSave={handleSubmit}
                  {...props}
                />
              </Form>
            )}
          </Formik>
        )}
      </Modal>
    </>
  );
};
